import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import isEqual from 'react-fast-compare';
import { FormProvider } from 'react-hook-form';
import { useSponsoredListingCampaignForm } from 'src/components/SponsoredListings';
import { useAdCampaignsDataContext } from 'src/contexts/AdCampaignsDataContext';
import { useUserHasAnyOfPermissions } from 'src/hooks/useUserHasAnyOfPermissions';
import {
  Permission,
  SponsoredListingsCampaign,
  SponsoredListingsCampaignInput,
} from 'src/WebApiController';

export type CampaignRowFormContextProps = {
  onSubmit: (data: SponsoredListingsCampaignInput) => void;
  isLoading?: boolean;
};

const CampaignRowFormContext = createContext<CampaignRowFormContextProps>({
  onSubmit: () => {},
});

export const useCampaignRowFormContext = () =>
  useContext(CampaignRowFormContext);

export const CampaignRowForm = ({
  campaign,
  children,
}: {
  campaign: SponsoredListingsCampaign;
  children: React.ReactNode;
}) => {
  const hasAdPlatformEditPermission = useUserHasAnyOfPermissions(
    Permission.AdPlatform_CampaignsEdit,
    Permission.AdPlatform_Edit
  );

  const [isLoading, setIsLoading] = useState(false);
  const { refreshData } = useAdCampaignsDataContext();
  const onBeforeSubmit = useCallback(() => setIsLoading(true), []);
  const onAfterSubmit = useCallback(
    (success: boolean) => {
      setIsLoading(false);
      if (success) {
        refreshData();
      }
    },
    [refreshData]
  );

  const { methods, onSubmit, getDefaultValue } =
    useSponsoredListingCampaignForm({
      eventId: campaign?.eventId,
      campaign,
      onBeforeSubmit,
      onAfterSubmit,
      onError: undefined,
      resetFormAfterSubmitSuccess: true,
    });

  useEffect(() => {
    if (campaign) {
      const newDefaultValues = getDefaultValue(campaign);
      if (
        !methods.formState.isDirty &&
        !isEqual(newDefaultValues, methods.formState.defaultValues)
      ) {
        methods.reset(newDefaultValues);
      }
    }
  }, [campaign, getDefaultValue, methods]);

  if (!hasAdPlatformEditPermission) {
    return <>{children}</>;
  }

  return (
    <CampaignRowFormContext.Provider value={{ onSubmit, isLoading }}>
      <FormProvider {...methods}>{children}</FormProvider>
    </CampaignRowFormContext.Provider>
  );
};
